/**
 * 209's follow-up (the owner: "you can't get onto the pad as a player. I think we still can't walk on surfaces of buildings"):
 * the structures with a walkable top, laid on their planet as stands (core/standGround.ts reads them into the person's ground).
 * A kind in STAND_TOPS stands where it is seated (its `baseLift`, core/buildSeat.ts — so only at the person scale, on the fine
 * ground): its top `topPeople` over its base, over its footprint (`halfXPeople` × `halfZPeople` in its frame, core/hits.ts
 * structureFrame), with a skirt round its edge — a step (STAND.stepSkirtPeople) where its top stands within STAND.stepPeople of
 * the lowest ground round its rim, else a ramp STAND.rampRun people out for each person it rises. 209's footing follow-up (lane
 * F): the buildings' plinth pads stand too — footingOf: the HQ's, the team buildings' by builder (a modular one's layout), the
 * post's — seated on the highest ground under them (core/buildSeat.ts) as the pad is. Pure; at the setup, after the builds are
 * seated, and again when a structure is built or goes (core/stands.ts withStands is pure: the match re-lays them).
 */
import type { GameState, Stand, StandCells, StructureState, StructureType, TerrainEdit } from '../types';
import { buildCellIndex, prepareCells } from './cells';
import { dacos } from './dmath';
import type { BuildingLayout } from '../types/modules';
import { personSetOf, PERSON_BODY_HEIGHT } from './scaleProfile';
import { groundAt } from './fineTerrain';
import { getSurfaceRadius } from './surface';
import { structureFrame } from './hits';
import { STAND, STAND_TOPS, PAD_OVERHANG, PLINTH_HALF_PEOPLE } from '../constants/action';
import { buildingsByFunction, builderOf, isPlanetBuilding } from './buildingScale';
import { layoutFor, layoutExtent } from './modules';
import { dcos, dsin } from './dmath';
import { vecAdd, vecNormalize, vecScale, vecSub } from '../math';

/** A walkable top's footprint in its frame (people) and its top over its seated base. */
export interface Footing { halfXPeople: number; halfZPeople: number; topPeople: number }

/**
 * 209's footing follow-up (lane F): the plinth pad's half (people, before PAD_OVERHANG) under a building sized by function —
 * a team building's by its builder (constants/action.ts PLINTH_HALF_PEOPLE, the model's extent; its layout's extent where the
 * buildings are modular, 63.14), the HQ's, the post's; null for anything else (the landing pad's deck is its own row in
 * STAND_TOPS, the small builds stand by their discs). Pure, no config: the renderer draws the pad from it too, so the drawn
 * pad and the sim's stand are one number.
 */
export function plinthHalfPeople(s: Pick<StructureState, 'type' | 'builder'>, byFunction: boolean, layout: BuildingLayout | null): number | null {
  if (!byFunction || !isPlanetBuilding(s.type) || s.type === 'landingPad') return null;
  if (s.type === 'ecotourism') return PLINTH_HALF_PEOPLE.hq;
  if (s.type === 'antenna') return PLINTH_HALF_PEOPLE.post;
  if (layout) { const e = layoutExtent(layout); return Math.max(e.halfX, e.halfZ); }
  return PLINTH_HALF_PEOPLE.team[builderOf(s.builder ?? 0)];
}

/**
 * Structure `s`'s walkable top, if its kind has one: its own row (STAND_TOPS: the landing pad's deck) or its plinth pad —
 * a square of plinthHalfPeople × PAD_OVERHANG, its top the building's base (a modular building's ground floor is that pad:
 * the kit's cells sit on it). Null where it has none, or the buildings are not sized by function.
 */
export function footingOf(s: Pick<StructureState, 'type' | 'builder'>, config: GameState['config'] | undefined): Footing | null {
  const own = STAND_TOPS[s.type];
  if (own) return own;
  if (!config) return null;
  const half = plinthHalfPeople(s, buildingsByFunction(config), layoutFor(s, config));
  return half === null ? null : { halfXPeople: half * PAD_OVERHANG, halfZPeople: half * PAD_OVERHANG, topPeople: 0 };
}

/** Structure `s`'s stand, or null (no walkable top, not seated, no fine ground). */
export function standOf(state: Pick<GameState, 'planets' | 'config'>, s: StructureState): Stand | null {
  const kind = footingOf(s, state.config), planet = state.planets[s.planetIndex], ps = personSetOf(state as GameState);
  if (!kind || s.baseLift === undefined || !planet || !(ps.fineAmplitude > 0)) return null;
  const person = PERSON_BODY_HEIGHT * ps.k, dir = vecNormalize(s.surfaceDir), { tx, tz } = structureFrame(s, dir);
  const halfX = kind.halfXPeople * person, halfZ = kind.halfZPeople * person;
  const top = getSurfaceRadius(planet, dir) + s.baseLift + kind.topPeople * person;
  // (its rise over the lowest ground round its rim, the ground without stands)
  let low = Infinity;
  for (let i = 0; i < STAND.rimPoints; i++) {
    const a = (2 * Math.PI * i) / STAND.rimPoints, x = dcos(a), z = dsin(a), k = 1 / Math.max(Math.abs(x), Math.abs(z));   // (a point on the rectangle's rim)
    const off = vecAdd(vecScale(tx, (x * k * halfX) / planet.radius), vecScale(tz, (z * k * halfZ) / planet.radius));
    low = Math.min(low, groundAt(planet, vecNormalize(vecAdd(dir, off)), 'ground', ps));   // (71 step 0: the ground's level without decks, not a planet copied without its stands)
  }
  const rise = top - low, skirt = rise <= STAND.stepPeople * person ? STAND.stepSkirtPeople * person : Math.max(STAND.stepSkirtPeople * person, rise * STAND.rampRun);
  const reach = Math.sqrt(halfX * halfX + halfZ * halfZ) + skirt;
  return { id: s.id, dir, tx, tz, halfX, halfZ, skirt, top, cosReach: dcos(Math.min(Math.PI, reach / planet.radius)) };
}

/** The stands' cells per face edge: a few hundred cells, a stand or so in each at a base of hundreds of footings; the table is tiny. */
const STAND_CELLS_N = 8;
/**
 * From this many stands a planet files them by cell; under it the loop is cheaper (measured, Oct 19, under load so ratios
 * only: the cell query's own cost is about 50 stands' dot products — at 300 stands the index answers in a fifth of the
 * loop's time, at 1,000 a twentieth, at 10 the loop wins three to one).
 */
const STAND_INDEX_FROM = 48;

/** 209's footing follow-up: a planet's stands filed by cell, with the widest reach as the query's angle (core/standGround.ts); none under STAND_INDEX_FROM stands. */
export function standCellsFor(stands: readonly Stand[]): StandCells | undefined {
  if (stands.length < STAND_INDEX_FROM) return undefined;
  prepareCells(STAND_CELLS_N);
  let cos = 1;
  for (const s of stands) cos = Math.min(cos, s.cosReach);
  return { index: buildCellIndex(stands.length, i => stands[i].dir, STAND_CELLS_N), angle: dacos(Math.max(-1, Math.min(1, cos))) };
}

/** The state with each planet's stands laid (the same state when no structure stands). */
export function withStands(state: GameState): GameState {
  const by = new Map<number, Stand[]>();
  for (const s of [...state.structures].sort((a, b) => a.id - b.id)) {
    const st = standOf(state, s);
    if (st) by.set(s.planetIndex, [...(by.get(s.planetIndex) ?? []), st]);
  }
  if (!by.size && !state.planets.some(p => p.stands?.length)) return state;
  return { ...state, planets: state.planets.map((p, i) => {
    const stands = by.get(i);
    if (stands) { const standCells = standCellsFor(stands); return { ...p, stands, ...(standCells ? { standCells } : {}) }; }
    if (!p.stands?.length) return p;
    const { stands: _s, standCells: _c, ...bare } = p;   // (its last stand's structure is gone)
    return bare;
  }) };
}

/**
 * 209's footing follow-up: the stands re-laid when the structures that stand have changed — one built or gone since the
 * setup (as a dig changes the ground when it is dug) — else the same state. A cheap check a tick with builds: the standing
 * structures' ids against the stands'.
 */
export function restand(state: GameState): GameState {
  const want = new Map<number, number[]>();
  for (const s of state.structures) if (footingOf(s, state.config) && s.baseLift !== undefined) (want.get(s.planetIndex) ?? want.set(s.planetIndex, []).get(s.planetIndex)!).push(s.id);
  let changed = false;
  state.planets.forEach((p, i) => {
    const have = p.stands ?? [], ids = want.get(i) ?? [];
    if (have.length !== ids.length) { changed = true; return; }
    const set = new Set(ids);
    for (const st of have) if (!set.has(st.id)) { changed = true; return; }
  });
  return changed ? withStands(state) : state;
}

/**
 * 69.41 (DECISIONS 254 (2), the owner: "footings somehow doing the same thing and getting taller than the thing they surround"):
 * the stands re-laid after ground edits that reached one — a crater, a mound, a dig or a craterlet within a stand's reach of
 * it, by its radius. A stand's top is laid at the ground under it then (its coarse ground and the structure's `baseLift`) and
 * was laid again only when the standing structures changed (`restand`), so after a crater the walkable deck and its skirt
 * stood where the ground had been while the drawn plinth (the coarse ground and the same `baseLift`) went down with the
 * crater. Re-laid, the deck is the drawn plinth's again. Else the same state.
 */
export function restandAfter(state: GameState, edits: ReadonlyArray<Pick<TerrainEdit, 'kind' | 'planetIndex' | 'point' | 'radius'>>): GameState {
  for (const e of edits) {
    if (e.kind === 'terrace') continue;
    const p = state.planets[e.planetIndex];
    if (!p?.stands?.length) continue;
    const at = vecNormalize(vecSub(e.point, p.position)), reach = e.radius / p.radius;
    for (const st of p.stands) {
      const c = st.dir.x * at.x + st.dir.y * at.y + st.dir.z * at.z;
      if (dacos(Math.max(-1, Math.min(1, c))) <= dacos(Math.max(-1, Math.min(1, st.cosReach))) + reach) return withStands(state);
    }
  }
  return state;
}

export { STAND, STAND_TOPS };
